import clsx from "clsx";
import { useRef, useState, type CSSProperties } from "react";
import type { TColorWithWeights } from "../../scss";
import { getColor } from "../../scss";
import { useOnClickOutside } from "../../utils";
import { cssUnit } from "../types";
import styles from "./Tooltip.module.scss";

export const useTooltip = () => {
  const [open, setOpen] = useState(false);

  return {
    isOpen: open,
    open() {
      setOpen(true);
    },
    close() {
      setOpen(false);
    },
  };
};

export interface TooltipProps {
  text: string | JSX.Element;
  children?: JSX.Element | string;
  color?: string;
  bgColor?: TColorWithWeights;
  width?: number;
  height?: number;
  position?: "top" | "bottom" | "left" | "right";
  trigger?: "hover" | "click" | "manual";
  containerClassName?: string;
  tipClassName?: string;
  isOpen?: boolean;
  tipStyle?: CSSProperties;
  onManualClose?: () => void;
}

const Tooltip = (props: TooltipProps) => {
  const {
    isOpen,
    text,
    children,
    color = "#585858",
    bgColor = "neutral-50",
    height,
    width,
    position = "top-center",
    trigger = "hover",
    containerClassName,
    tipClassName,
    tipStyle = {},
    onManualClose = () => {},
  } = props;
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowToolTip] = useState(false);
  const onClose = () => {
    setShowToolTip(false);
  };
  const onOpen = () => {
    setShowToolTip(true);
  };
  useOnClickOutside([tooltipRef], () => {
    if (trigger === "click") onClose();
    if (trigger === "manual") onManualClose();
  });

  const style = {
    backgroundColor: getColor(bgColor),
    ...(height && { height: cssUnit(height) }),
    ...(width && { width: cssUnit(width) }),
    color: color,
    ...tipStyle,
  };

  return (
    <div
      ref={tooltipRef}
      className={clsx(styles["tooltip-container"], containerClassName)}
      onClick={() => {
        if (trigger === "click") onOpen();
      }}
      onMouseEnter={() => {
        if (trigger === "hover") onOpen();
      }}
      onMouseLeave={() => trigger === "hover" && onClose()}
    >
      {children && (
        <div className={styles["tooltip-container__content"]}>{children}</div>
      )}
      <div
        style={style}
        className={clsx(
          tipClassName,
          styles["tooltip-container__text"],
          (trigger == "manual" ? isOpen : showTooltip) &&
            text &&
            styles["tooltip-container__text--show"],
          styles[`tooltip-container--position-${position}`],
        )}
      >
        <div className={clsx(styles["tooltip-container__text__inner"])}>
          {text}
        </div>
      </div>
    </div>
  );
};

export default Tooltip;
